import React from 'react';
import { Input, Select } from 'antd';
import { ACCESS_CODE_TYPE_OPTIONS, MAX_LIST_SIZE } from '../constants';
import { AccessCodeDraft } from '../types';

interface Props {
    codes: AccessCodeDraft[];
    /** 只读模式 (无编辑权限)。 */
    readonly?: boolean;
    onChange: (next: AccessCodeDraft[]) => void;
}

/**
 * AOI 门禁码编辑器 (AOI 级门禁, 落 biz_aoi_attribute.access_codes)。
 *
 * <p>门禁归属 AOI 本身 (社区大门 / 楼门 / 电梯 密码), 不挂具体地址, 所以没有"选地址"这一步,
 * 也不需要前端做 diff: 保存时整个列表随 upsert 全量覆盖, 元素身份由服务端重算的 uid 表达。
 *
 * <p><b>UI 只开放"类型 + Code 值"两个字段</b> (对齐产品 demo)。状态 (默认待验证) 和备注仍在契约里,
 * 只是不给编辑入口 —— 将来要放开时加控件即可, 不动接口。
 *
 * <p>行内布局跟 PointListEditor 一致 (卡片 + 底部虚线新增按钮), 保持抽屉里各字段视觉统一。
 */
const AccessCodeEditor: React.FC<Props> = ({ codes, readonly = false, onChange }) => {
    const update = (idx: number, patch: Partial<AccessCodeDraft>) => {
        onChange(codes.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
    };
    const remove = (idx: number) => {
        onChange(codes.filter((_, i) => i !== idx));
    };
    const add = () => {
        // 状态 / 备注 前端不开放 (按 demo 只给类型 + 值), 但契约里仍带默认值:
        // 状态默认待验证, 备注留空 —— 后续要放开 UI 时不用改契约。
        onChange([...codes, { id: null, codeType: '', codeValue: '', available: 'PENDING_VERIFICATION', remark: null }]);
    };

    return (
        <div>
            <label className="mb-2 block text-sm" style={{ color: 'rgba(0,0,0,.88)' }}>
                门禁 Code
                <span style={{ color: '#a5adb9', fontWeight: 500 }}>（可多个）</span>
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {codes.map((c, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Select
                            size="small"
                            style={{ width: 120, flex: 'none' }}
                            placeholder="类型"
                            value={c.codeType || undefined}
                            disabled={readonly}
                            onChange={(v) => update(i, { codeType: v ?? '' })}
                            options={ACCESS_CODE_TYPE_OPTIONS}
                        />
                        <Input
                            size="small"
                            style={{ flex: 1 }}
                            placeholder="Code 值"
                            value={c.codeValue}
                            disabled={readonly}
                            maxLength={64}
                            onChange={(e) => update(i, { codeValue: e.target.value })}
                        />
                        {!readonly && (
                            <button
                                type="button"
                                title="删除"
                                onClick={() => remove(i)}
                                style={{
                                    flex: 'none',
                                    width: 24,
                                    height: 24,
                                    border: 'none',
                                    borderRadius: 6,
                                    background: 'transparent',
                                    color: '#a5adb9',
                                    fontSize: 13,
                                    cursor: 'pointer',
                                }}
                            >✕</button>
                        )}
                    </div>
                ))}
                {readonly && codes.length === 0 && (
                    <div className="text-xs text-gray-400">暂无门禁码</div>
                )}
                {!readonly && (
                    <button
                        type="button"
                        onClick={add}
                        disabled={codes.length >= MAX_LIST_SIZE}
                        style={{
                            height: 34,
                            border: '1px dashed #cdd6e2',
                            borderRadius: 8,
                            background: '#fff',
                            color: 'rgba(0,0,0,.65)',
                            fontWeight: 600,
                            fontSize: 12,
                            cursor: codes.length >= MAX_LIST_SIZE ? 'not-allowed' : 'pointer',
                        }}
                    >＋ 添加 Code</button>
                )}
            </div>
        </div>
    );
};

export default AccessCodeEditor;
